import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, ImagePlus, X } from 'lucide-react';
import { cn } from './cn';
import { Button } from './Button';
import {
  UPLOAD_FORMAT_GUIDE,
  dimensionGuideFor,
  validateImageFile,
  type ImageUploadQueue,
  type MediaCategory,
  type UploadSlot,
} from '@/lib/upload';

export interface ImageGalleryFieldProps {
  queue: ImageUploadQueue;
  name: string;
  category: MediaCategory;
  value: string[];
  onChange: (urls: string[]) => void;
  max?: number | undefined;
  /**
   * 해당 surface 의 권장 규격 안내(예: "권장 1000×1000px · 1:1"). 근거가 있는 화면만 전달한다.
   * 포맷/용량 가이드(`UPLOAD_FORMAT_GUIDE`)는 항상 노출되므로 여기엔 픽셀/비율만 담는다.
   */
  guide?: string | undefined;
}

interface PendingEntry {
  kind: 'pending';
  file: File;
  previewUrl: string;
  key: string;
}

interface ExistingEntry {
  kind: 'existing';
  url: string;
}

type GalleryEntry = ExistingEntry | PendingEntry;

let pendingSeq = 0;

const toSlot = (entry: GalleryEntry): UploadSlot =>
  entry.kind === 'existing'
    ? { kind: 'existing', url: entry.url }
    : { kind: 'pending', file: entry.file };

const existingUrls = (entries: ReadonlyArray<GalleryEntry>): string[] =>
  entries.filter((e): e is ExistingEntry => e.kind === 'existing').map((e) => e.url);

/**
 * 다중 이미지 갤러리 필드. `<input multiple>` 로 한 번에 여러 장 선택, 순서변경(↑↓),
 * 개별삭제, max 제한. 기존 URL 과 신규 pending File 을 단일 정렬 리스트로 관리하며
 * flush() 시 그 순서대로 최종 string[] 을 산출한다.
 */
export const ImageGalleryField = ({
  queue,
  name,
  category,
  value,
  onChange,
  max,
  guide,
}: ImageGalleryFieldProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [pending, setPending] = useState<PendingEntry[]>([]);
  const [localError, setLocalError] = useState<string | null>(null);
  const [isDragging, setDragging] = useState(false);

  const entries = useMemo<GalleryEntry[]>(
    () => [...value.map((url): ExistingEntry => ({ kind: 'existing', url })), ...pending],
    [value, pending],
  );

  const slotsRef = useRef<ReadonlyArray<UploadSlot>>([]);
  slotsRef.current = entries.map(toSlot);

  const { register, unregister } = queue;
  // 슬롯(이미지 추가/삭제/순서변경)이 바뀔 때마다 재등록하여 큐의 pending 집계(hasPending)를
  // 갱신한다. register 는 동일 name 덮어쓰기 + refreshPending 이라 idempotent. (마운트 1회만
  // 등록하면 파일 추가 후에도 hasPending 이 갱신되지 않아 제출 게이트가 막힌다 — ImageUploadField 동일.)
  useEffect(() => {
    register(name, { category, multiple: true, getSlots: () => slotsRef.current });
  }, [register, name, category, entries]);
  useEffect(() => () => unregister(name), [unregister, name]);

  useEffect(
    () => () => {
      pending.forEach((p) => URL.revokeObjectURL(p.previewUrl));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const remaining = typeof max === 'number' ? max - entries.length : Infinity;

  const acceptFiles = (files: FileList) => {
    const incoming = Array.from(files);
    const room = Math.max(0, remaining);
    const accepted = incoming.slice(0, room);

    const invalid = accepted.map(validateImageFile).find((m): m is string => m !== null);
    if (invalid) {
      setLocalError(invalid);
      return;
    }
    if (incoming.length > room) {
      setLocalError(
        typeof max === 'number'
          ? `최대 ${max}장까지 등록할 수 있습니다. 일부 파일은 추가되지 않았습니다.`
          : null,
      );
    } else {
      setLocalError(null);
    }

    const newEntries: PendingEntry[] = accepted.map((file) => ({
      kind: 'pending',
      file,
      previewUrl: URL.createObjectURL(file),
      key: `pending-${pendingSeq++}`,
    }));
    if (newEntries.length > 0) setPending((prev) => [...prev, ...newEntries]);
  };

  const onSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) acceptFiles(e.target.files);
    e.target.value = '';
  };

  const onDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files?.length) acceptFiles(e.dataTransfer.files);
  };

  const onDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setDragging(true);
  };

  const onDragLeave = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setDragging(false);
  };

  const applyOrder = (next: GalleryEntry[]) => {
    onChange(existingUrls(next));
    setPending(next.filter((e): e is PendingEntry => e.kind === 'pending'));
  };

  const removeAt = (idx: number) => {
    const target = entries[idx];
    if (target?.kind === 'pending') URL.revokeObjectURL(target.previewUrl);
    applyOrder(entries.filter((_, i) => i !== idx));
  };

  const move = (idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    if (target < 0 || target >= entries.length) return;
    const next = [...entries];
    const [item] = next.splice(idx, 1);
    if (item) next.splice(target, 0, item);
    applyOrder(next);
  };

  const previewFor = (entry: GalleryEntry): string =>
    entry.kind === 'existing' ? entry.url : entry.previewUrl;

  const reachedMax = typeof max === 'number' && entries.length >= max;

  // ID57 — 명시 guide 가 없으면 surface(category) 권장 px/비율을 자동 적용(SSoT). 포맷/용량은 항상 표기.
  const effectiveGuide = guide ?? dimensionGuideFor(category);

  return (
    <div className="flex flex-col gap-3">
      <label
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        className={cn(
          'flex cursor-pointer flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed px-4 py-6 text-center text-[13px] transition-colors',
          reachedMax && 'pointer-events-none opacity-50',
        )}
        style={{
          borderColor: isDragging ? 'var(--content-strong)' : 'var(--divider-strong)',
          background: isDragging ? 'var(--surface-1)' : 'var(--surface-0)',
          color: 'var(--content-muted)',
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={onSelect}
          disabled={reachedMax}
          className="sr-only"
        />
        <ImagePlus size={18} aria-hidden="true" />
        <span style={{ color: 'var(--content-strong)' }}>
          이미지를 끌어다 놓거나 클릭하여 여러 장 선택
        </span>
        <span className="text-[11px]">
          {effectiveGuide ? `${effectiveGuide} · ${UPLOAD_FORMAT_GUIDE}` : UPLOAD_FORMAT_GUIDE}
          {typeof max === 'number' ? ` · ${entries.length}/${max}장` : ''}
        </span>
      </label>

      {entries.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {entries.map((entry, idx) => (
            <li
              key={entry.kind === 'existing' ? `existing-${entry.url}` : entry.key}
              className="flex items-center gap-3 rounded-md p-2"
              style={{ background: 'var(--surface-0)', border: '1px solid var(--divider-strong)' }}
            >
              <div
                className="flex h-16 w-16 flex-shrink-0 items-center justify-center overflow-hidden rounded"
                style={{ background: 'var(--surface-1)', border: '1px solid var(--divider-strong)' }}
              >
                <img
                  src={previewFor(entry)}
                  alt=""
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </div>
              <span className="flex-1 truncate text-[12px]" style={{ color: 'var(--content-default)' }}>
                {idx + 1}. {entry.kind === 'pending' ? `${entry.file.name} (업로드 대기)` : entry.url}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => move(idx, -1)}
                  disabled={idx === 0}
                  aria-label="위로 이동"
                >
                  <ArrowUp size={14} />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => move(idx, 1)}
                  disabled={idx === entries.length - 1}
                  aria-label="아래로 이동"
                >
                  <ArrowDown size={14} />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeAt(idx)}
                  aria-label="삭제"
                >
                  <X size={14} style={{ color: 'var(--signal-danger)' }} />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {localError ? (
        <p role="alert" className="text-[11px]" style={{ color: 'var(--signal-danger)' }}>
          {localError}
        </p>
      ) : null}
    </div>
  );
};
